import { Injectable } from '@angular/core'
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn } from '@angular/forms'
import { DateService } from './date.service'

interface Shift {
  days_start: number
  days_end: number
  hours_start: string
  hours_end: string
}

@Injectable({
  providedIn: 'root',
})
export class DynamicFormControlValidatorService {
  _statusFunc: () => boolean = () => false
  requiredFormRules = new Map<FormControl | AbstractControl, [ValidatorFn[], ValidatorFn[]]>()
  constructor(private dateService: DateService) {}
  init(statusFunc: () => boolean) {
    this._statusFunc = statusFunc
  }
  switchFormValidator() {
    this.requiredFormRules.forEach(([ValidatorsAsTrue, ValidatorsAsFalse]: [ValidatorFn[], ValidatorFn[]], control: FormControl | AbstractControl) => {
      control.clearValidators()
      if (this._statusFunc() && ValidatorsAsTrue.length) {
        control.setValidators(ValidatorsAsTrue)
      } else if (!this._statusFunc() && ValidatorsAsFalse.length) {
        control.setValidators(ValidatorsAsFalse)
      }
      control.updateValueAndValidity()
    })
  }

  set(f: FormControl | AbstractControl, validators: [ValidatorFn[], ValidatorFn[]]): void {
    this.requiredFormRules.set(f, validators)
  }

  trimValidator(control: FormControl) {
    const value = control?.value as string | undefined
    if (value?.startsWith(' ')) {
      return {
        trimError: { value: 'control has leading whitespace' },
      }
    }
    if (value?.endsWith(' ')) {
      return {
        trimError: { value: 'control has trailing whitespace' },
      }
    }

    return null
  }

  timeRangeValidator(startingTimeControlName: string, endingTimeControlName: string, fromControlName: string, toControlName: string): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const fromValue = formGroup.get(fromControlName)?.value as number | string
      const toValue = formGroup.get(toControlName)?.value as number | string
      const fromDay = typeof fromValue === 'number' && !isNaN(fromValue) ? fromValue : new Date(fromValue).toDateString()

      const toDay = typeof toValue === 'number' && !isNaN(toValue) ? toValue : new Date(toValue).toDateString()

      if (fromDay == toDay) {
        const hoursStartControl = formGroup.get(startingTimeControlName)
        const hoursEndControl = formGroup.get(endingTimeControlName)

        if (!hoursStartControl || !hoursEndControl) {
          return null // controls not available, validation should pass
        }

        const hoursStart = hoursStartControl.value as string
        const hoursEnd = hoursEndControl.value as string

        // Validate the time format (hh:mm)
        const timeRegex = /^(?:2[0-3]|[01][0-9]):[0-5][0-9]$/

        if (!timeRegex.test(hoursStart) || !/^(?:[01]\d|2[0-3]):[0-5]\d|(24:00)$/.test(hoursEnd)) {
          return { invalidTimeFormat: true }
        }

        // Special case: Allow finish time to be '24:00'
        if (hoursEnd !== '24:00') {
          // Convert time strings to Date objects for easier comparison
          const startDate = new Date(`2000-01-01T${hoursStart}`)
          const endDate = new Date(`2000-01-01T${hoursEnd}`)

          // Check if the start time is before the end time
          if (startDate >= endDate) {
            return { invalidTimeRange: true }
          }
        }
      }
      const hoursStartControl = formGroup.get(startingTimeControlName)
      const hoursEndControl = formGroup.get(endingTimeControlName)

      if (!hoursStartControl || !hoursEndControl) {
        return null // controls not available, validation should pass
      }

      const hoursStart = hoursStartControl.value as string
      const hoursEnd = hoursEndControl.value as string

      // Validate the time format (hh:mm)
      const timeRegex = /^(?:2[0-3]|[01][0-9]):[0-5][0-9]$/

      if (!timeRegex.test(hoursStart) || !/^(?:[01]\d|2[0-3]):[0-5]\d|(24:00)$/.test(hoursEnd)) {
        return { invalidTimeFormat: true }
      }
      return null
    }
  }

  startEndTimeValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const startingTime = group.get('starting_time')?.value as string | number | undefined
      const closingTime = group.get('closing_time')?.value as string | number | undefined

      if (startingTime && closingTime) {
        const start = new Date(startingTime)
        const end = new Date(closingTime)

        // Set seconds and milliseconds to 0 for comparison
        start.setSeconds(0, 0)
        end.setSeconds(0, 0)

        return end <= start ? { timeRangeInvalid: true } : null
      }

      return null
    }
  }

  // Custom validator function to check for collision
  shiftCollisionValidator(shifts: Shift[]): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const currentShift = formGroup.value as Shift
      if (this.isTimeValid(formGroup, 'hours_start', 'hours_end')) {
        // If the control value is empty, consider it as valid (no collision)
        return { notValid: true }
      }

      // Check for collisions with other shifts
      const collision = shifts.some((shift) => {
        // Compare days
        const daysCollision = currentShift.days_start <= shift.days_end && currentShift.days_end >= shift.days_start

        if (daysCollision) {
          const startHour = new Date(`2000-01-01T${shift.hours_start}`)
          const endHour = new Date(`2000-01-01T${shift.hours_end}`)
          const rowStartHour = new Date(`2000-01-01T${currentShift.hours_start}`)
          const rowEndHour = new Date(`2000-01-01T${currentShift.hours_end}`)

          // Adjust endHour for shifts that cross midnight
          if (shift.hours_end < shift.hours_start) {
            endHour.setDate(endHour.getDate() + 1)
          }

          // Adjust rowEndHour for shifts that cross midnight
          if (currentShift.hours_end < currentShift.hours_start) {
            rowEndHour.setDate(rowEndHour.getDate() + 1)
          }

          return (startHour >= rowStartHour && startHour < rowEndHour) || (endHour > rowStartHour && endHour <= rowEndHour) || (startHour <= rowStartHour && endHour >= rowEndHour)
        }

        return false
      })

      // Return validation error if there is a collision, null otherwise
      return collision ? { collision: true } : null
    }
  }

  isTimeValid(formGroup: AbstractControl, startingTimeControlName: string, endingTimeControlName: string) {
    const hoursStartControl = formGroup.get(startingTimeControlName)
    const hoursEndControl = formGroup.get(endingTimeControlName)

    if (!hoursStartControl || !hoursEndControl) {
      return null // controls not available, validation should pass
    }

    const hoursStart = hoursStartControl.value as string
    const hoursEnd = hoursEndControl.value as string

    // Validate the time format (hh:mm)
    const timeRegex = /^(?:2[0-3]|[01][0-9]):[0-5][0-9]$/

    if (!timeRegex.test(hoursStart) || !/^(?:[01]\d|2[0-3]):[0-5]\d|(24:00)$/.test(hoursEnd)) {
      return { invalidTimeFormat: true }
    }
    return null
  }

  atLeastOneInputRequiredValidator(inputs: string[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const filledInputs = inputs.filter((input) => {
        const value = control.get(input)?.value as string | undefined
        return value && value.length
      })
      if (filledInputs.length >= 1)
        inputs.forEach((v) => {
          control.get(v)?.setErrors(null)
        })
      return filledInputs.length >= 1 ? null : { atLeastOneRequired: true }
    }
  }
}
